import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";

import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import Hr from "components/atoms/Hr/Hr";
import CardNew from "components/molecules/Card/CardNew";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";

const QuoteService = function ({ data, loadingData }) {
  const { id } = useParams();
  const [isCustomBroker] = useCustomBrokerPermission();

  const {
    invited_providers,
    invited_freight_modes,
    delivery_service_level,
    service_requirement_display,
    incoterm,
    incoterm_instructions,
    insurance,
    insurance_instructions,
    import_clearance,
    import_clearance_instructions,
    export_clearance,
    export_clearance_instructions,
    transit_clearance,
    transit_clearance_instructions,
    special_requirements_json,
    customs_broker,
  } = data?.service || {};

  // WEB-8876: We want to display winning provider for custom brokers
  const { data: shipmentDetails = {} } = useGetShipmentDetails(id, true, {
    enabled: !!id && isCustomBroker,
    refetchOnMount: false,
  });
  const winningProvider = shipmentDetails?.organization?.provider_company;

  return (
    <CardNew
      className="mb-15 font-b3"
      isLoading={loadingData}
      id="customs-div"
      title={<FormattedMessage id="quote-request-detail.service" defaultMessage="Service" />}
    >
      {isCustomBroker && winningProvider && (
        <Row className="mb-2 hide-during-print">
          <Col className="text-secondary">
            <FormattedMessage id="general.provider" defaultMessage="Provider" />
          </Col>
          <Col>{winningProvider}</Col>
        </Row>
      )}
      {invited_providers && (
        <Row className="mb-2 hide-during-print">
          <Col className="text-secondary">
            <FormattedMessage id="general.provider" defaultMessage="Provider" />
          </Col>
          <Col>{invited_providers ? invited_providers.join(", ") : "-"}</Col>
        </Row>
      )}
      {customs_broker && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="general.customsBroker" defaultMessage="Customs broker" />
          </Col>
          <Col>
            {customs_broker?.team_name} ({customs_broker?.company})
          </Col>
        </Row>
      )}
      {invited_freight_modes && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="general.freightMode" defaultMessage="Freight mode" />
          </Col>
          <Col>{invited_freight_modes ? invited_freight_modes.join(", ") : "-"}</Col>
        </Row>
      )}
      {delivery_service_level && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.serviceLevel" defaultMessage="Service level" />
          </Col>
          <Col>{delivery_service_level || "-"}</Col>
        </Row>
      )}
      {service_requirement_display && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.serviceRequirement" defaultMessage="Service requirement" />
          </Col>
          <Col>{service_requirement_display || "-"}</Col>
        </Row>
      )}
      {incoterm && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.incoterms" defaultMessage="Incoterms" />
          </Col>
          <Col>{incoterm === "NONE" ? "None" : incoterm || "-"}</Col>
        </Row>
      )}
      {incoterm_instructions && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.service.incotermInstructions" defaultMessage="Incoterm instructions" />
          </Col>
          <Col>{incoterm_instructions || "-"}</Col>
        </Row>
      )}
      {insurance && insurance_instructions && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="general.insurance" defaultMessage="Insurance" />
          </Col>
          <Col>{`${insurance ? "Yes" : "No"} ${insurance_instructions || ""}`}</Col>
        </Row>
      )}
      {export_clearance && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="tableHeaders.exportClearance" defaultMessage="Export clearance" />
          </Col>
          <Col>{export_clearance || "-"}</Col>
        </Row>
      )}
      {export_clearance_instructions && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="quote-request-detail.exportClearanceInstructions"
              defaultMessage="Export clearance instructions"
            />
          </Col>
          <Col>{export_clearance_instructions}</Col>
        </Row>
      )}
      {transit_clearance && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.transitClearance" defaultMessage="Transit clearance" />
          </Col>
          <Col>{transit_clearance || "-"}</Col>
        </Row>
      )}
      {transit_clearance_instructions && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="quote-request-detail.transitClearanceInstructions"
              defaultMessage="Transit clearance instructions"
            />
          </Col>
          <Col>{transit_clearance_instructions}</Col>
        </Row>
      )}
      {import_clearance && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.importClearance" defaultMessage="Import clearance" />
          </Col>
          <Col>{import_clearance || "-"}</Col>
        </Row>
      )}
      {import_clearance_instructions && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="quote-request-detail.importClearanceInstructions"
              defaultMessage="Import clearance instructions"
            />
          </Col>
          <Col>{import_clearance_instructions}</Col>
        </Row>
      )}

      {!loadingData &&
        data &&
        special_requirements_json &&
        Object.keys(special_requirements_json).map((key, i) => {
          const { label, additional_information, value } = special_requirements_json[key];
          return (
            <span key={`${label}_${value}`}>
              {i === 0 && <Hr />}
              <Row className="mb-2">
                <Col className="text-secondary">{label}</Col>
                <Col>{value ? `Yes${additional_information ? `, ${additional_information}` : ""}` : "No"}</Col>
              </Row>
            </span>
          );
        })}
    </CardNew>
  );
};

QuoteService.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
};

export default QuoteService;
